import { Injectable } from '@angular/core';
import {
  Resolve,
  RouterStateSnapshot,
  ActivatedRouteSnapshot,
  Router,
} from '@angular/router';
import { ProductService } from '@app/shared/services/product/product.service';
import { filter, take, skip } from 'rxjs/operators';
import { OrderService } from './order.service';
import { Store } from '@ngrx/store';
import {
  MyOrdersStoreService,
  OrderHeader,
  OrderStatus,
} from '@usf/ngrx-order';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { MerchandisingService } from '@shared/services/merchandising/merchandising.service';
import { CustomerStoreService } from '@app/ngrx-customer/services';
import { LoadingSpinnerService } from '@shared/services/loading-spinner/loading-spinner.service';
import { HomeService } from '@app/home/services/home.service';
import { BaseOrderResolver } from './base-order-resolver';
import { OrderHeaderService } from './order-header.service';

@Injectable({
  providedIn: 'root',
})
export class OrderPageDeepLinkResolver extends BaseOrderResolver {
  constructor(
    store: Store,
    productService: ProductService,
    orderService: OrderService,
    merchandisingService: MerchandisingService,
    spinner: LoadingSpinnerService,
    customerStoreService: CustomerStoreService,
    homeService: HomeService,
    myOrdersStoreService: MyOrdersStoreService,
    router: Router,
  ) {
    super(
      store,
      productService,
      orderService,
      merchandisingService,
      spinner,
      customerStoreService,
      homeService,
      myOrdersStoreService,
      router,
    );
  }

  static isOrderEditable = (
    header: OrderHeader,
    allowVendorShipped: boolean,
  ) => {
    if (
      allowVendorShipped &&
      header?.orderType === 'VS' &&
      header.orderModifiable === true &&
      header.orderStatus !== OrderStatus.SUBMITTING
    ) {
      var minutesElapsed = 0;
      if (header?.orderPlacedDtm) {
        const orderPlacedDtm = new Date(header.orderPlacedDtm);

        const now = new Date();
        const differenceInMilliseconds =
          now.getTime() - orderPlacedDtm.getTime();

        minutesElapsed = differenceInMilliseconds / (1000 * 60);
      } else if (header.updateDtm) {
        const updateDtm = new Date(header.updateDtm);

        const now = new Date();
        const differenceInMilliseconds = now.getTime() - updateDtm.getTime();

        minutesElapsed = differenceInMilliseconds / (1000 * 60);
      }
      return minutesElapsed <= 30;
    } else {
      return (
        header !== undefined &&
        header !== null &&
        header.orderModifiable === true &&
        header.orderType !== 'VS' &&
        header.orderStatus !== OrderStatus.SUBMITTING
      );
    }
  };

  resolve(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<number[]> {
    this.spinnerPromise = this.spinner.createSpinnerModal(true);
    const orderId = route.paramMap.get('orderId');
    if (orderId) {
      const [customerNumber, departmentNumber, divisionNumber] =
        orderId.split(':');
      const customer$ = this.homeService
        .getCustomerWithCustomerNumber(parseInt(customerNumber, 10))
        .pipe(
          filter(customer => !!customer),
          take(1),
        );
      const order$ = this.myOrdersStoreService.getOrderWithId(orderId);
      const restrictToOG$ = this.orderService.getRestrictedNationalFlag$();
      const modifyVSOrdersEnabled$ = this.orderService.getModifyVsOrdersFlag$();

      combineLatest([customer$, order$, restrictToOG$, modifyVSOrdersEnabled$])
        .pipe(
          filter(
            ([customer, order, restrictToOG, modifyVSOrdersEnabled]) =>
              !!customer,
          ),
          skip(1),
          take(1),
        )
        .subscribe(([customer, order, restrictToOG, modifyVSOrdersEnabled]) => {
          if (
            (order &&
              OrderHeaderService.isOrderEditable(
                order?.orderHeader,
                modifyVSOrdersEnabled,
              ) &&
              !restrictToOG) ||
            order?.orderHeader?.orderStatus === OrderStatus.IN_PROGRESS
          ) {
            this.orderService.makeOrderCurrentOrder(order, customer);
          } else {
            this.spinner.dismissSpinnerModal();
            this.router.navigate(['/404']);
            return;
          }
        });
    }
    this.initializeReadyForProductSelection();
    this.fetchAndLoadFrequentlyBoughtTogether();
    return this.loadProducts();
  }
}
